import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';

const MAX_VISIBLE_SHORTCUTS = 10;

const ComposerHeader = ({
  hasOpenTickets,
  mergePillTitle,
  mergePillLabel,
  mergePromptMessage,
  displayQuickCommands = [],
  onShortcutClick,
  onOpenShortcutsModal,
  isDark
}) => {
  const [showMorePopover, setShowMorePopover] = useState(false);
  const morePopoverRef = useRef(null);
  const moreButtonRef = useRef(null);

  const validCommands = Array.isArray(displayQuickCommands) ? displayQuickCommands.filter(Boolean) : [];
  const visibleCommands = validCommands.slice(0, MAX_VISIBLE_SHORTCUTS);
  const hiddenCommands = validCommands.slice(MAX_VISIBLE_SHORTCUTS);
  const hasMore = hiddenCommands.length > 0;

  // Cerrar el popover al hacer clic fuera o pulsar Escape
  useEffect(() => {
    if (!showMorePopover) return;

    const handleClickOutside = e => {
      if (
        morePopoverRef.current &&
        !morePopoverRef.current.contains(e.target) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(e.target)
      ) {
        setShowMorePopover(false);
      }
    };

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        setShowMorePopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMorePopover]);

  return (
    <div className="d-flex align-items-center justify-content-between px-3 pt-2 pb-1 flex-wrap gap-2">
      {/* Sección de Atajos */}
      <div className="d-flex align-items-center gap-1.5 flex-wrap">
        <span
          className="d-flex align-items-center gap-1"
          style={{
            fontSize: '0.78rem',
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 500
          }}
        >
          <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b', fontSize: '0.76rem' }} />
          <span>Atajos:</span>
        </span>

        {hasOpenTickets && (
          <button
            type="button"
            title={mergePillTitle}
            onClick={() => onShortcutClick(mergePromptMessage)}
            className="btn btn-sm p-0 px-2 py-0.5 shadow-none"
            style={{
              fontSize: '0.74rem',
              fontWeight: 600,
              borderRadius: '16px',
              backgroundColor: '#fef3c7',
              color: '#b45309',
              border: '1px solid #fde68a',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            {mergePillLabel}
          </button>
        )}

        {visibleCommands.map((cmd, idx) => {
          const label = cmd.vComando || cmd.label || '';
          const text = cmd.vTexto || cmd.text || '';
          const title = cmd.vTitulo || cmd.title || text;
          const cleanLabel = label.startsWith('/') ? label : `/${label}`;
          return (
            <button
              key={cmd.idAtajo || idx}
              type="button"
              title={title}
              onClick={() => onShortcutClick(text)}
              className="btn btn-sm p-0 px-2 py-0.5 shadow-none"
              style={{
                fontSize: '0.74rem',
                fontWeight: 500,
                fontFamily: 'monospace, sans-serif',
                borderRadius: '16px',
                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#cbd5e1' : '#475569',
                border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#e2e8f0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = isDark ? '#1e293b' : '#f1f5f9';
              }}
            >
              {cleanLabel}
            </button>
          );
        })}

        {/* Indicador .... con popover si hay más de 10 atajos */}
        {hasMore && (
          <div className="position-relative d-inline-block">
            <button
              ref={moreButtonRef}
              type="button"
              title={`Ver ${hiddenCommands.length} atajos más`}
              onClick={() => setShowMorePopover(prev => !prev)}
              className="btn btn-sm p-0 px-2 py-0.5 shadow-none d-inline-flex align-items-center gap-1"
              style={{
                fontSize: '0.74rem',
                fontWeight: 700,
                borderRadius: '16px',
                backgroundColor: showMorePopover
                  ? (isDark ? '#334155' : '#e2e8f0')
                  : (isDark ? '#1e293b' : '#f1f5f9'),
                color: isDark ? '#38bdf8' : '#0284c7',
                border: showMorePopover
                  ? (isDark ? '1px solid #38bdf8' : '1px solid #0284c7')
                  : (isDark ? '1px solid #334155' : '1px solid #cbd5e1'),
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => {
                if (!showMorePopover) {
                  e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#e2e8f0';
                  e.currentTarget.style.borderColor = isDark ? '#64748b' : '#94a3b8';
                }
              }}
              onMouseLeave={e => {
                if (!showMorePopover) {
                  e.currentTarget.style.backgroundColor = isDark ? '#1e293b' : '#f1f5f9';
                  e.currentTarget.style.borderColor = isDark ? '#334155' : '#cbd5e1';
                }
              }}
            >
              <span style={{ letterSpacing: '1px' }}>....</span>
              <span style={{ fontSize: '0.68rem', opacity: 0.85 }}>+{hiddenCommands.length}</span>
            </button>

            {/* Popover flotante ligero para visualizar y usar el resto de atajos */}
            {showMorePopover && (
              <div
                ref={morePopoverRef}
                className="position-absolute shadow-lg rounded-3 p-2"
                style={{
                  bottom: '100%',
                  left: 0,
                  marginBottom: '8px',
                  minWidth: '260px',
                  maxWidth: '340px',
                  maxHeight: '260px',
                  overflowY: 'auto',
                  backgroundColor: isDark ? '#0f172a' : '#ffffff',
                  border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                  zIndex: 1050,
                  boxShadow: isDark
                    ? '0 10px 25px -5px rgba(0, 0, 0, 0.6), 0 8px 10px -6px rgba(0, 0, 0, 0.6)'
                    : '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-between pb-1 mb-1 border-bottom px-1"
                  style={{ borderColor: isDark ? '#1e293b' : '#f1f5f9' }}
                >
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: isDark ? '#94a3b8' : '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Más atajos ({hiddenCommands.length})
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowMorePopover(false)}
                    className="btn btn-link p-0 text-muted shadow-none"
                    style={{ fontSize: '0.72rem', lineHeight: 1 }}
                    title="Cerrar"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <div className="d-flex flex-column gap-1">
                  {hiddenCommands.map((cmd, hIdx) => {
                    const label = cmd.vComando || cmd.label || '';
                    const text = cmd.vTexto || cmd.text || '';
                    const title = cmd.vTitulo || cmd.title || text;
                    const cleanLabel = label.startsWith('/') ? label : `/${label}`;
                    return (
                      <button
                        key={cmd.idAtajo || hIdx}
                        type="button"
                        onClick={() => {
                          onShortcutClick(text);
                          setShowMorePopover(false);
                        }}
                        className="btn btn-sm d-flex align-items-center justify-content-between text-start p-1.5 rounded-2 shadow-none"
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = isDark ? '#1e293b' : '#f1f5f9';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title={title}
                      >
                        <span
                          className="px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{
                            fontSize: '0.72rem',
                            fontFamily: 'monospace, sans-serif',
                            fontWeight: 600,
                            backgroundColor: isDark ? '#1e293b' : '#e2e8f0',
                            color: isDark ? '#38bdf8' : '#0284c7'
                          }}
                        >
                          {cleanLabel}
                        </span>
                        <span
                          className="text-truncate ms-2"
                          style={{
                            fontSize: '0.73rem',
                            color: isDark ? '#cbd5e1' : '#475569',
                            maxWidth: '190px'
                          }}
                        >
                          {title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          title="Administrar o crear atajos"
          onClick={onOpenShortcutsModal}
          className="btn btn-sm p-0 px-2 py-0.5 shadow-none d-inline-flex align-items-center gap-1"
          style={{
            fontSize: '0.74rem',
            fontWeight: 600,
            borderRadius: '16px',
            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.16)' : '#ecfdf5',
            color: isDark ? '#34d399' : '#059669',
            border: isDark ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid #a7f3d0',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = isDark ? 'rgba(16, 185, 129, 0.28)' : '#d1fae5';
            e.currentTarget.style.borderColor = isDark ? '#34d399' : '#6ee7b7';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = isDark ? 'rgba(16, 185, 129, 0.16)' : '#ecfdf5';
            e.currentTarget.style.borderColor = isDark ? 'rgba(16, 185, 129, 0.35)' : '#a7f3d0';
            e.currentTarget.style.transform = 'none';
          }}
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.62rem' }} />
          <span>Atajo</span>
        </button>
      </div>
    </div>
  );
};

ComposerHeader.propTypes = {
  hasOpenTickets: PropTypes.bool,
  mergePillTitle: PropTypes.string,
  mergePillLabel: PropTypes.string,
  mergePromptMessage: PropTypes.string,
  displayQuickCommands: PropTypes.array,
  onShortcutClick: PropTypes.func.isRequired,
  onOpenShortcutsModal: PropTypes.func.isRequired,
  isDark: PropTypes.bool
};

export default ComposerHeader;
